export interface DateRange {
  startDate: string  // YYYY-MM-DD
  endDate: string    // YYYY-MM-DD
}

export type PeriodPreset = "week" | "month" | "year" | "all" | "custom"

export interface PeriodEntry {
  preset: PeriodPreset
  startDate: string
  endDate: string
}

const toDateStr = (d: Date) => d.toISOString().split("T")[0]

const VALID_PRESETS = new Set(["week", "month", "year", "all", "custom"])

export const PRESET_DAYS: Record<Exclude<PeriodPreset, "custom">, number> = {
  week:  7,
  month: 30,
  year:  365,
  all:   Infinity,
}

export const addDays = (dateStr: string, days: number): string => {
  if (!dateStr) return ""
  const d = new Date(dateStr)
  d.setDate(d.getDate() + days)
  return toDateStr(d)
}

export const presetToRange = (
  preset: Exclude<PeriodPreset, "custom">,
  allTimeStart = "2000-01-01",
): DateRange => {
  const today  = new Date()
  const endStr = toDateStr(today)
  if (preset === "all") return { startDate: allTimeStart, endDate: endStr }
  const daysBack = preset === "week" ? 7 : preset === "month" ? 30 : 365
  const start = new Date()
  start.setDate(start.getDate() - daysBack + 1)
  return { startDate: toDateStr(start), endDate: endStr }
}

const defaultPeriods = (allTimeStart: string): PeriodEntry[] => [
  { preset: "month", ...presetToRange("month", allTimeStart) },
]

export const readStoredPeriods = (
  storageKey: string,
  allTimeStart = "2000-01-01",
): PeriodEntry[] => {
  try {
    const stored = sessionStorage.getItem(storageKey)
    if (!stored) return defaultPeriods(allTimeStart)
    const parsed = JSON.parse(stored)
    // 新形式: PeriodEntry[]
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map((p, i) => {
        const preset = VALID_PRESETS.has(p.preset) ? (p.preset as PeriodPreset) : "custom"
        const range = (i === 0 && preset !== "custom")
          ? presetToRange(preset as Exclude<PeriodPreset, "custom">, allTimeStart)
          : { startDate: p.startDate ?? "", endDate: p.endDate ?? "" }
        return { preset, ...range }
      })
    }
    // 旧形式: { preset, startDate?, endDate? } → 配列に移行
    if (parsed.preset) {
      const preset = VALID_PRESETS.has(parsed.preset) ? (parsed.preset as PeriodPreset) : "custom"
      const range = preset === "custom" && parsed.startDate && parsed.endDate
        ? { startDate: parsed.startDate, endDate: parsed.endDate }
        : presetToRange(
            (preset === "custom" ? "month" : preset) as Exclude<PeriodPreset, "custom">,
            allTimeStart,
          )
      return [{ preset, ...range }]
    }
  } catch {}
  return defaultPeriods(allTimeStart)
}

export const savePeriods = (storageKey: string, periods: PeriodEntry[]): void => {
  try { sessionStorage.setItem(storageKey, JSON.stringify(periods)) } catch {}
}

export const readStoredRange = (storageKey: string, allTimeStart = "2000-01-01"): DateRange => {
  const periods = readStoredPeriods(storageKey, allTimeStart)
  return { startDate: periods[0].startDate, endDate: periods[0].endDate }
}
